<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import type { DataTablePageEvent, DataTableRowClickEvent } from "primevue/datatable";
import { postFetchAllTickets, postCloseTicket, postFetchAllStatuses } from "src/api/queries/order-portal-bff";
import { useNotification } from "src/composables/useNotification";
import { useLayout } from "src/composables/useLayout";
import type { FetchTicketResponseFull } from "src/api/models/fetchTicketResponseFull";
import { TicketEditDialog } from "src/components/ticket-edit";

const { tableSizeClass } = useLayout();

const READY_FOR_PROCESSING_STATUS_NAME = "ready for processing";

const tickets = ref<FetchTicketResponseFull[]>([]);
const selectedTickets = ref<FetchTicketResponseFull[]>([]);
const loading = ref(false);
const closing = ref(false);
const dialogVisible = ref(false);
const selectedTicketId = ref<string>();
const readyStatusId = ref<string>();

const first = ref(0);
const rowsPerPage = ref(50);
const totalPages = ref(0);
const totalRecords = computed(() => totalPages.value * rowsPerPage.value);

const { showSuccess, showError, showWarning } = useNotification();

async function resolveReadyStatusId() {
  if (readyStatusId.value) return readyStatusId.value;

  const statusesResponse = await postFetchAllStatuses();
  const readyStatus = statusesResponse?.find(
    (s) => s.name === READY_FOR_PROCESSING_STATUS_NAME,
  );

  if (!readyStatus?.id) {
    showError({ message: "Could not find 'ready for processing' status" });
    return undefined;
  }

  readyStatusId.value = readyStatus.id;
  return readyStatus.id;
}

async function fetchTickets(page = 1, limit = rowsPerPage.value) {
  loading.value = true;
  try {
    const statusId = await resolveReadyStatusId();
    if (!statusId) return;

    const response = await postFetchAllTickets(
      { status_id: statusId },
      { page, limit },
    );
    tickets.value = response.data ?? [];
    totalPages.value = response.total_pages ?? 0;
  } catch {
    showError({ message: "Failed to load tickets" });
  } finally {
    loading.value = false;
  }
}

function onPage(event: DataTablePageEvent) {
  first.value = event.first;
  rowsPerPage.value = event.rows;
  fetchTickets(event.page + 1, event.rows);
}

async function closeSelected() {
  if (selectedTickets.value.length === 0) return;

  closing.value = true;
  try {
    const results = await Promise.allSettled(
      selectedTickets.value.map((ticket) => postCloseTicket(ticket.id!)),
    );

    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.filter((r) => r.status === "rejected").length;

    if (failed === 0) {
      showSuccess({ message: `${succeeded} ticket(s) closed successfully` });
    } else if (succeeded > 0) {
      showWarning({ message: `${succeeded} closed, ${failed} failed` });
    } else {
      showError({ message: `Failed to close ${failed} ticket(s)` });
    }

    selectedTickets.value = [];
    first.value = 0;
    await fetchTickets(1, rowsPerPage.value);
  } finally {
    closing.value = false;
  }
}

function openTicket(ticket: FetchTicketResponseFull) {
  selectedTicketId.value = ticket.id;
  dialogVisible.value = true;
}

function onRowClick(event: DataTableRowClickEvent) {
  const target = event.originalEvent.target as HTMLElement;
  const selectionCell = target.closest('td[data-p-selection-column="true"]');

  if (selectionCell) {
    // Click was in the checkbox cell area but may have missed the actual input — toggle it
    const checkbox = selectionCell.querySelector<HTMLInputElement>('input[type="checkbox"]');
    if (checkbox && target !== checkbox) {
      checkbox.click();
    }
    return;
  }

  openTicket(event.data as FetchTicketResponseFull);
}

watch(dialogVisible, (visible) => {
  if (!visible) selectedTicketId.value = undefined;
}, { flush: 'post' });

onMounted(fetchTickets);
</script>

<template>
  <div>
    <DataTable
      v-model:selection="selectedTickets"
      v-model:first="first"
      :value="tickets"
      :loading="loading"
      :class="`p-datatable-${tableSizeClass}`"
      class="border-round"
      data-key="id"
      paginator
      lazy
      :rows="rowsPerPage"
      :total-records="totalRecords"
      :rows-per-page-options="[25, 50, 100]"
      striped-rows
      removable-sort
      @row-click="onRowClick"
      @page="onPage"
    >
      <template #header>
        <div class="flex justify-between items-center">
          <h2 class="text-xl font-semibold m-0">Ready for Processing</h2>
          <Button
            label="Close selected"
            icon="pi pi-check"
            severity="danger"
            :disabled="selectedTickets.length === 0 || closing"
            :loading="closing"
            @click="closeSelected"
          />
        </div>
      </template>
      <Column selection-mode="multiple" header-style="width: 3rem" />
      <Column field="number" header="Number" sortable />
      <Column field="title" header="Title" sortable />
      <Column header="Priority" sortable sort-field="priority.name">
        <template #body="{ data }">
          {{ data.priority?.name }}
        </template>
      </Column>
      <Column header="Type" sortable sort-field="type.name">
        <template #body="{ data }">
          {{ data.type?.name }}
        </template>
      </Column>
      <Column header="Assignee" sortable sort-field="assignee.name">
        <template #body="{ data }">
          {{ data.assignee?.name }}
        </template>
      </Column>
      <Column header="Site" sortable sort-field="site.name">
        <template #body="{ data }">
          {{ data.site?.name }}
        </template>
      </Column>
      <Column field="created_at" header="Created" sortable />
      <Column header="" header-style="width: 4rem" :exportable="false">
        <template #body="{ data }">
          <Button
            icon="pi pi-eye"
            severity="info"
            text
            rounded
            aria-label="Open ticket"
            @click.stop="openTicket(data)"
          />
        </template>
      </Column>

      <template #empty>
        <div class="text-center p-4">No tickets ready for processing.</div>
      </template>
    </DataTable>

    <TicketEditDialog
      v-if="selectedTicketId"
      v-model="dialogVisible"
      :ticket-id="selectedTicketId"
      @update:ticket="() => fetchTickets()"
    />
  </div>
</template>

<style scoped>
:deep(tr[data-pc-section="bodyrow"]) {
  cursor: pointer;
}

:deep(tr[data-pc-section="bodyrow"]:hover) {
  background: var(--p-datatable-row-hover-background) !important;
}
</style>
